import { useState, useEffect } from "react";
import { Building2, Users, Settings as SettingsIcon, Mail, Moon, Sun, X, Edit, Trash2, Key, Copy, UserCheck, UserX, Clock, MapPin, Briefcase, Plus, UserMinus, ArrowRightLeft, CheckSquare, Square, Loader2, AlertTriangle, Shield, RotateCcw, LayoutDashboard, Sparkles, Link2, ClipboardList } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useTheme } from "@/contexts/ThemeContext";
import { useBusiness } from "@/contexts/BusinessContext";
import BusinessOnboardingWizard from "@/components/BusinessOnboardingWizard";
import TaskDataRetention from "@/components/settings/TaskDataRetention";
import CompanyProfileCard from "@/components/settings/CompanyProfileCard";
import { NavRow } from "@/components/settings/SettingsUI";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Available roles in the system
const ROLE_OPTIONS = [
  { value: "employee", label: "Employee" },
  { value: "team_leader", label: "Team Leader" },
  { value: "project_manager", label: "Project Manager" },
  { value: "manager", label: "Manager" },
  { value: "admin", label: "Admin" }
];

// Full role options including Super Admin (only shown to Super Admins)
const FULL_ROLE_OPTIONS = [
  { value: "employee", label: "Employee" },
  { value: "team_leader", label: "Team Leader" },
  { value: "project_manager", label: "Project Manager" },
  { value: "manager", label: "Manager" },
  { value: "admin", label: "Admin" },
  { value: "super_admin", label: "Super Admin" }
];

// Role badge colors
const ROLE_COLORS = {
  super_admin: "bg-red-100 text-red-700",
  admin: "bg-purple-100 text-purple-700",
  manager: "bg-indigo-100 text-indigo-700",
  project_manager: "bg-blue-100 text-blue-700",
  team_leader: "bg-cyan-100 text-cyan-700",
  employee: "bg-green-100 text-green-700",
  user: "bg-green-100 text-green-700" // Legacy support
};

// Helper to get role display label
const getRoleLabel = (role) => {
  const option = FULL_ROLE_OPTIONS.find(r => r.value === role);
  return option ? option.label : (role === "user" ? "Employee" : role);
};

// ─── Colorful section card (2-column settings layout) ───────────────────────
const CARD_THEMES = {
  violet:  { grad: "from-violet-500 to-fuchsia-500", shadow: "shadow-violet-200/60 dark:shadow-none" },
  rose:    { grad: "from-rose-500 to-red-500",       shadow: "shadow-rose-200/60 dark:shadow-none" },
  amber:   { grad: "from-amber-400 to-orange-500",   shadow: "shadow-amber-200/60 dark:shadow-none" },
  emerald: { grad: "from-emerald-500 to-teal-500",   shadow: "shadow-emerald-200/60 dark:shadow-none" },
  sky:     { grad: "from-sky-500 to-indigo-500",     shadow: "shadow-sky-200/60 dark:shadow-none" },
  indigo:  { grad: "from-indigo-500 to-violet-500",  shadow: "shadow-indigo-200/60 dark:shadow-none" },
};

const ColorCard = ({ theme = "indigo", icon: Icon, title, description, badge, span = false, flush = false, className = "", children, testId }) => {
  const t = CARD_THEMES[theme] || CARD_THEMES.indigo;
  return (
    <section
      className={`${span ? "lg:col-span-2" : ""} ${className} bg-white dark:bg-slate-800 rounded-3xl overflow-hidden border border-slate-100 dark:border-slate-700 shadow-xl ${t.shadow}`}
      data-testid={testId}
    >
      <div className={`bg-gradient-to-r ${t.grad} px-5 py-4 flex items-center gap-3`}>
        {Icon && (
          <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5 text-white" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h2 className="text-white font-heading font-bold text-lg leading-tight">{title}</h2>
          {description && <p className="text-white/80 text-xs mt-0.5">{description}</p>}
        </div>
        {badge}
      </div>
      {flush ? (
        <div className="divide-y divide-slate-100 dark:divide-slate-700/60">{children}</div>
      ) : (
        <div className="p-5">{children}</div>
      )}
    </section>
  );
};

const Settings = () => {
  const { theme, toggleTheme } = useTheme();
  const { businessId } = useBusiness();
  const [teamMembers, setTeamMembers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [invites, setInvites] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [passwordResetUser, setPasswordResetUser] = useState(null);
  const [generatedPassword, setGeneratedPassword] = useState("");
  
  // Business Management State
  const [businesses, setBusinesses] = useState([]);
  const [businessDialogOpen, setBusinessDialogOpen] = useState(false);
  const [editBusinessDialogOpen, setEditBusinessDialogOpen] = useState(false);
  const [deleteBusinessDialogOpen, setDeleteBusinessDialogOpen] = useState(false);
  const [assignUserDialogOpen, setAssignUserDialogOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [businessUsers, setBusinessUsers] = useState([]);
  const [unassignedUsers, setUnassignedUsers] = useState([]);
  const [newBusiness, setNewBusiness] = useState({
    name: "",
    city: "",
    industry: "",
    status: "active",
  });
  
  // Business Onboarding Wizard State
  const [onboardingWizardOpen, setOnboardingWizardOpen] = useState(false);
  
  // Lead Transfer State
  const [transferLeadsDialogOpen, setTransferLeadsDialogOpen] = useState(false);
  const [leadsForTransfer, setLeadsForTransfer] = useState([]);
  const [selectedLeadsForTransfer, setSelectedLeadsForTransfer] = useState([]);
  const [targetBusinessId, setTargetBusinessId] = useState("");
  const [transferLoading, setTransferLoading] = useState(false);
  const [sourceBusinessFilter, setSourceBusinessFilter] = useState("all");
  
  // Permission Management State
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [selectedUserForPermission, setSelectedUserForPermission] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [availablePages, setAvailablePages] = useState([]);
  const [permissionLoading, setPermissionLoading] = useState(false);
  
  // User Business Assignment State (Super Admin)
  const [assignBusinessDialogOpen, setAssignBusinessDialogOpen] = useState(false);
  const [selectedUserForAssignment, setSelectedUserForAssignment] = useState(null);
  const [selectedBusinessForAssignment, setSelectedBusinessForAssignment] = useState("");
  const [allBusinessesList, setAllBusinessesList] = useState([]);
  
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    role: "employee",
  });
  const [newInvite, setNewInvite] = useState({
    email: "",
    role: "employee",
  });

  const fetchPendingUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/pending-users`, {
        credentials: "include",
      });
      if (response.ok) {
        const users = await response.json();
        setPendingUsers(users);
      }
    } catch (error) {
      console.error("Error fetching pending users:", error);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: "include",
      });
      if (response.ok) {
        const user = await response.json();
        setCurrentUser(user);
        // If admin, fetch pending users
        if (user.role === "admin") {
          fetchPendingUsers();
        }
      }
    } catch (error) {
      console.error("Error fetching current user:", error);
    }
  };

  const fetchTeamData = async () => {
    try {
      const bizParam = businessId ? `?business_id=${businessId}` : '';
      const [membersRes, invitesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/team/members${bizParam}`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/team/invites${bizParam}`, { credentials: "include" }),
      ]);

      if (membersRes.ok) {
        const members = await membersRes.json();
        setTeamMembers(members);
      }

      if (invitesRes.ok) {
        const invitesData = await invitesRes.json();
        setInvites(invitesData);
      }
    } catch (error) {
      console.error("Error fetching team data:", error);
    }
  };

  useEffect(() => {
    fetchCurrentUser();
    fetchTeamData();
  }, [businessId]);

  // ============ BUSINESS MANAGEMENT FUNCTIONS ============
  
  const fetchBusinesses = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/businesses`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setBusinesses(data);
        setAllBusinessesList(data); // Also store for assignment dropdown
      }
    } catch (error) {
      console.error("Error fetching businesses:", error);
    }
  };

  const fetchBusinessUsers = async (businessId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/businesses/${businessId}/users`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setBusinessUsers(data);
      }
    } catch (error) {
      console.error("Error fetching business users:", error);
    }
  };

  const fetchUnassignedUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/unassigned`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setUnassignedUsers(data);
      }
    } catch (error) {
      console.error("Error fetching unassigned users:", error);
    }
  };

  const handleCreateBusiness = async () => {
    if (!newBusiness.name.trim()) {
      toast.error("Business name is required");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/businesses`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newBusiness),
      });
      
      if (response.ok) {
        toast.success("Business created successfully!");
        setBusinessDialogOpen(false);
        setNewBusiness({ name: "", city: "", industry: "", status: "active" });
        fetchBusinesses();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create business");
      }
    } catch (error) {
      toast.error("Error creating business");
    }
  };

  const handleUpdateBusiness = async () => {
    if (!selectedBusiness?.name?.trim()) {
      toast.error("Business name is required");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/businesses/${selectedBusiness.business_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: selectedBusiness.name,
          city: selectedBusiness.city,
          industry: selectedBusiness.industry,
          status: selectedBusiness.status,
        }),
      });
      
      if (response.ok) {
        toast.success("Business updated successfully!");
        setEditBusinessDialogOpen(false);
        setSelectedBusiness(null);
        fetchBusinesses();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update business");
      }
    } catch (error) {
      toast.error("Error updating business");
    }
  };

  const handleDeleteBusiness = async () => {
    if (!selectedBusiness) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/businesses/${selectedBusiness.business_id}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success("Business deleted successfully!");
        setDeleteBusinessDialogOpen(false);
        setSelectedBusiness(null);
        fetchBusinesses();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete business");
      }
    } catch (error) {
      toast.error("Error deleting business");
    }
  };

  const handleAssignUser = async (userId) => {
    if (!selectedBusiness) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/businesses/${selectedBusiness.business_id}/assign-user?user_id=${userId}`, {
        method: "POST",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success("User assigned successfully!");
        fetchBusinessUsers(selectedBusiness.business_id);
        fetchUnassignedUsers();
        fetchBusinesses();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to assign user");
      }
    } catch (error) {
      toast.error("Error assigning user");
    }
  };

  const handleUnassignUser = async (userId) => {
    if (!selectedBusiness) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/businesses/${selectedBusiness.business_id}/unassign-user?user_id=${userId}`, {
        method: "POST",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success("User unassigned successfully!");
        fetchBusinessUsers(selectedBusiness.business_id);
        fetchUnassignedUsers();
        fetchBusinesses();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to unassign user");
      }
    } catch (error) {
      toast.error("Error unassigning user");
    }
  };

  const openAssignUserDialog = (business) => {
    setSelectedBusiness(business);
    fetchBusinessUsers(business.business_id);
    fetchUnassignedUsers();
    setAssignUserDialogOpen(true);
  };

  // Fetch businesses when super_admin
  useEffect(() => {
    if (currentUser?.role === "super_admin") {
      fetchBusinesses();
    }
  }, [currentUser]);

  const handleSendInvite = async () => {
    if (!newInvite.email) {
      toast.error("Please enter an email address");
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/team/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newInvite),
      });

      if (response.ok) {
        toast.success("Invitation sent successfully!");
        setInviteDialogOpen(false);
        setNewInvite({ email: "", role: "employee" });
        fetchTeamData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send invitation");
      }
    } catch (error) {
      toast.error("Failed to send invitation");
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/team/invite/${inviteId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Invitation cancelled");
        fetchTeamData();
      }
    } catch (error) {
      toast.error("Failed to cancel invitation");
    }
  };

  const handleEditUser = (user) => {
    setEditingUser({
      user_id: user.user_id,
      name: user.name,
      email: user.email,
      role: user.role,
      // Payroll profile fields (surface on the salary slip)
      employee_id: user.employee_id || "",
      date_of_joining: user.date_of_joining || "",
      designation: user.designation || "",
    });
    setEditUserDialogOpen(true);
  };

  const handleUpdateUser = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/team/member/${editingUser.user_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: editingUser.name,
          role: editingUser.role,
          employee_id: editingUser.employee_id || "",
          date_of_joining: editingUser.date_of_joining || "",
          designation: editingUser.designation || "",
        }),
      });

      if (response.ok) {
        toast.success("User updated successfully!");
        setEditUserDialogOpen(false);
        setEditingUser(null);
        fetchTeamData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update user");
      }
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/team/member/${userId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("User deleted successfully!");
        fetchTeamData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete user");
      }
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const handleGeneratePassword = async (user) => {
    setPasswordResetUser(user);
    try {
      const response = await fetch(`${BACKEND_URL}/api/team/member/${user.user_id}/generate-password`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedPassword(data.temporary_password);
        setPasswordDialogOpen(true);
        toast.success("Temporary password generated!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to generate password");
      }
    } catch (error) {
      toast.error("Failed to generate password");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Password copied to clipboard!");
  };

  // ============ USER BUSINESS ASSIGNMENT (Super Admin) ============
  
  const [selectedRoleForAssignment, setSelectedRoleForAssignment] = useState("");
  
  const openAssignBusinessDialog = (user) => {
    setSelectedUserForAssignment(user);
    setSelectedBusinessForAssignment(user.business_id || "");
    setSelectedRoleForAssignment(user.role || "employee");
    setAssignBusinessDialogOpen(true);
  };

  const handleAssignBusinessAndRole = async () => {
    if (!selectedUserForAssignment) return;
    
    try {
      const updateData = {};
      
      // Only include business_id if it changed
      if (selectedBusinessForAssignment !== (selectedUserForAssignment.business_id || "")) {
        updateData.business_id = selectedBusinessForAssignment || "none";
      }
      
      // Only include role if it changed
      if (selectedRoleForAssignment !== selectedUserForAssignment.role) {
        updateData.role = selectedRoleForAssignment;
      }
      
      if (Object.keys(updateData).length === 0) {
        toast.info("No changes to save");
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/users/${selectedUserForAssignment.user_id}/update`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updateData),
      });
      
      if (response.ok) {
        toast.success("User updated successfully!");
        setAssignBusinessDialogOpen(false);
        setSelectedUserForAssignment(null);
        setSelectedRoleForAssignment("");
        fetchTeamData();
        if (canManageBusinesses) {
          fetchBusinesses();
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update user");
      }
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const handleAssignBusiness = async () => {
    if (!selectedUserForAssignment) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/${selectedUserForAssignment.user_id}/update`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          business_id: selectedBusinessForAssignment || "none"
        }),
      });
      
      if (response.ok) {
        toast.success("User business assignment updated!");
        setAssignBusinessDialogOpen(false);
        setSelectedUserForAssignment(null);
        fetchTeamData();
        if (canManageBusinesses) {
          fetchBusinesses();
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update business assignment");
      }
    } catch (error) {
      toast.error("Failed to update business assignment");
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/${userId}/update`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRole }),
      });
      
      if (response.ok) {
        toast.success("User role updated!");
        fetchTeamData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update role");
      }
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/team/member`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newUser),
      });

      if (response.ok) {
        const data = await response.json();
        setPasswordResetUser({
          name: data.name,
          email: data.email,
        });
        setGeneratedPassword(data.temporary_password);
        setCreateUserDialogOpen(false);
        setNewUser({ name: "", email: "", role: "employee" });
        setPasswordDialogOpen(true);
        toast.success("User created successfully!");
        fetchTeamData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create user");
      }
    } catch (error) {
      toast.error("Failed to create user");
    }
  };

  const handleApproveUser = async (userId, userName) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/approve`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        toast.success(`${userName} has been approved!`);
        fetchPendingUsers();
        fetchTeamData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to approve user");
      }
    } catch (error) {
      toast.error("Failed to approve user");
    }
  };

  const handleRejectUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to reject ${userName}? They will not be able to access the system.`)) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/reject`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        toast.success(`${userName} has been rejected`);
        fetchPendingUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to reject user");
      }
    } catch (error) {
      toast.error("Failed to reject user");
    }
  };

  // ============ PERMISSION MANAGEMENT FUNCTIONS ============
  
  const fetchAvailablePages = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/pages`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setAvailablePages(data.page_details || []);
      }
    } catch (error) {
      console.error("Error fetching available pages:", error);
    }
  };

  const openPermissionDialog = async (user) => {
    setSelectedUserForPermission(user);
    setPermissionLoading(true);
    setPermissionDialogOpen(true);
    
    try {
      // Fetch available pages if not already loaded
      if (availablePages.length === 0) {
        await fetchAvailablePages();
      }
      
      // Fetch user's current permissions
      const response = await fetch(`${BACKEND_URL}/api/auth/permissions/${user.user_id}`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserPermissions(data.page_permissions || []);
      } else {
        toast.error("Failed to load user permissions");
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      toast.error("Failed to load permissions");
    } finally {
      setPermissionLoading(false);
    }
  };

  const handlePermissionToggle = (pageValue) => {
    setUserPermissions(prev => 
      prev.includes(pageValue)
        ? prev.filter(p => p !== pageValue)
        : [...prev, pageValue]
    );
  };

  const handleSavePermissions = async () => {
    if (!selectedUserForPermission) return;
    
    setPermissionLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/permissions/${selectedUserForPermission.user_id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ page_permissions: userPermissions }),
      });
      
      if (response.ok) {
        toast.success("Permissions updated successfully");
        setPermissionDialogOpen(false);
        setSelectedUserForPermission(null);
        fetchTeamData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update permissions");
      }
    } catch (error) {
      console.error("Error updating permissions:", error);
      toast.error("Failed to update permissions");
    } finally {
      setPermissionLoading(false);
    }
  };

  const handleResetPermissions = async () => {
    if (!selectedUserForPermission) return;
    
    setPermissionLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/permissions/${selectedUserForPermission.user_id}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserPermissions(data.page_permissions || []);
        toast.success("Permissions reset to role defaults");
      } else {
        toast.error("Failed to reset permissions");
      }
    } catch (error) {
      console.error("Error resetting permissions:", error);
      toast.error("Failed to reset permissions");
    } finally {
      setPermissionLoading(false);
    }
  };

  // ============ LEAD TRANSFER FUNCTIONS ============
  
  const fetchLeadsForTransfer = async (businessId = null) => {
    try {
      const url = businessId && businessId !== "all"
        ? `${BACKEND_URL}/api/admin/leads-for-transfer?business_id=${businessId}`
        : `${BACKEND_URL}/api/admin/leads-for-transfer`;
      
      const response = await fetch(url, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setLeadsForTransfer(data);
      }
    } catch (error) {
      console.error("Error fetching leads for transfer:", error);
      toast.error("Failed to load leads");
    }
  };

  const handleOpenTransferDialog = () => {
    setTransferLeadsDialogOpen(true);
    setSelectedLeadsForTransfer([]);
    setTargetBusinessId("");
    setSourceBusinessFilter("all");
    fetchLeadsForTransfer();
  };

  const handleSourceBusinessFilterChange = (value) => {
    setSourceBusinessFilter(value);
    setSelectedLeadsForTransfer([]);
    fetchLeadsForTransfer(value === "all" ? null : value);
  };

  const handleToggleLeadSelection = (leadId) => {
    setSelectedLeadsForTransfer(prev => 
      prev.includes(leadId)
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAllLeads = () => {
    if (selectedLeadsForTransfer.length === leadsForTransfer.length) {
      setSelectedLeadsForTransfer([]);
    } else {
      setSelectedLeadsForTransfer(leadsForTransfer.map(lead => lead.lead_id));
    }
  };

  const handleTransferLeads = async () => {
    if (selectedLeadsForTransfer.length === 0) {
      toast.error("Please select at least one lead to transfer");
      return;
    }
    
    if (!targetBusinessId) {
      toast.error("Please select a target business");
      return;
    }

    // Check if any selected lead is already in target business
    const targetBusiness = businesses.find(b => b.business_id === targetBusinessId);
    const leadsInTarget = leadsForTransfer.filter(
      lead => selectedLeadsForTransfer.includes(lead.lead_id) && lead.business_id === targetBusinessId
    );
    
    if (leadsInTarget.length > 0) {
      toast.error(`${leadsInTarget.length} lead(s) are already in the target business`);
      return;
    }

    const confirmMessage = `Are you sure you want to transfer ${selectedLeadsForTransfer.length} lead(s) to "${targetBusiness?.name}"?\n\nThis will also move all related:\n• Contacts\n• Tasks\n• Activities & History\n• Calendar Events`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setTransferLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/transfer-leads`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          lead_ids: selectedLeadsForTransfer,
          target_business_id: targetBusinessId,
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        setTransferLeadsDialogOpen(false);
        setSelectedLeadsForTransfer([]);
        setTargetBusinessId("");
        fetchBusinesses(); // Refresh business data
      } else {
        toast.error(data.detail || "Failed to transfer leads");
      }
    } catch (error) {
      console.error("Error transferring leads:", error);
      toast.error("Failed to transfer leads");
    } finally {
      setTransferLoading(false);
    }
  };

  const isSuperAdmin = currentUser?.role === "super_admin";
  const isAdmin = currentUser?.role === "admin" || isSuperAdmin;
  const canManageBusinesses = isSuperAdmin;
  const canManagePermissions = isSuperAdmin;

  // Get available role options based on current user's role
  const availableRoles = isSuperAdmin ? FULL_ROLE_OPTIONS : ROLE_OPTIONS;

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-10" data-testid="settings-page">
      {/* Blocks the legacy server-injected Lead Stage card — the Leads card below covers it */}
      <span id="stage-mgmt-card" className="hidden" />

      {/* Hero header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-violet-600 via-indigo-600 to-sky-500 px-8 py-9 shadow-xl shadow-indigo-200/60 dark:shadow-indigo-900/20">
        <div className="absolute -top-16 -right-10 w-56 h-56 rounded-full bg-white/10 pointer-events-none" />
        <div className="absolute -bottom-24 right-40 w-44 h-44 rounded-full bg-fuchsia-400/20 pointer-events-none" />
        <div className="absolute top-8 right-1/4 w-3 h-3 rounded-full bg-amber-300/80 pointer-events-none" />
        <div className="absolute bottom-8 right-16 w-2 h-2 rounded-full bg-emerald-300/80 pointer-events-none" />
        <div className="relative flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
            <SettingsIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-heading font-bold text-white tracking-tight">Settings</h1>
            <p className="text-sm text-indigo-100 mt-0.5">Configure your CRM — one place, everything.</p>
          </div>
        </div>
      </div>

      {/* Two-column card grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">

      {/* LEADS — nav-only rows */}
      {(currentUser?.role === "super_admin" || currentUser?.role === "admin" || currentUser?.role === "manager") && (
        <ColorCard theme="violet" icon={ArrowRightLeft} title="Leads" description="Configure the lead lifecycle" flush className="order-2" testId="section-leads">
          <NavRow icon={ArrowRightLeft} iconColor="violet" title="Lead stages" description="Add, rename, reorder pipeline stages" to="/settings/lead-stages" testId="row-lead-stages" />
          <NavRow icon={Link2} iconColor="teal" title="Lead sources" description="Manage source labels and colors" to="/settings/lead-sources" testId="row-lead-sources" />
          {(currentUser?.role === "super_admin" || currentUser?.role === "admin") && (
            <>
              <NavRow icon={Users} iconColor="emerald" title="Auto-assignment rules" description="Round-robin, weighted, or rule-based" to="/settings/lead-assignment" testId="row-lead-assignment" />
              <NavRow icon={ClipboardList} iconColor="indigo" title="Form builder" description="Custom fields, order, validation" to="/settings/lead-form-builder" testId="row-lead-form" />
            </>
          )}
        </ColorCard>
      )}

      {/* ACCESS & SECURITY — super admin (all businesses) + business admin (own business) */}
      {(currentUser?.role === "super_admin" || currentUser?.role === "admin") && (
        <ColorCard theme="rose" icon={Shield} title="Access & Security" description={currentUser?.role === "admin" ? "Manage permissions for your business" : "Control who sees what"} flush className="order-3" testId="section-access">
          <NavRow icon={Shield} iconColor="rose" title="Access control" description="Fine-grained page and module permissions per user" to="/settings/access-control" testId="row-access-control" />
        </ColorCard>
      )}
        {/* Pending User Approvals - Admin Only */}
        {isAdmin && pendingUsers.length > 0 && (
          <ColorCard
            theme="amber"
            icon={Clock}
            title="Pending User Approvals"
            description="New signups waiting for a decision"
            badge={<Badge className="bg-white/25 text-white border-0">{pendingUsers.length} pending</Badge>}
            span
            className="order-1"
            testId="pending-approvals-card"
          >
              <div className="space-y-3">
                {pendingUsers.map((user) => (
                  <div
                    key={user.user_id}
                    data-testid={`pending-user-${user.user_id}`}
                    className="flex items-center justify-between p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800"
                  >
                    <div className="flex items-center gap-3">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                      ) : (
                        <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center text-white font-semibold">
                          {user.name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-slate-900 dark:text-white">{user.name}</p>
                        <p className="text-sm text-slate-600 dark:text-slate-400">{user.email}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-500">
                          Signed up: {new Date(user.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handleApproveUser(user.user_id, user.name)}
                        data-testid={`approve-user-${user.user_id}`}
                        className="bg-green-600 hover:bg-green-700 text-white rounded-full px-4"
                      >
                        <UserCheck className="w-4 h-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => handleRejectUser(user.user_id, user.name)}
                        data-testid={`reject-user-${user.user_id}`}
                        variant="outline"
                        className="border-red-200 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full px-4"
                      >
                        <UserX className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
          </ColorCard>
        )}

        {/* Team Management with Invitations */}
        <ColorCard theme="sky" icon={Users} title="Team Management" description="Members, roles, and invitations" span className="order-6" testId="team-management-card">
          <div className="space-y-4">
            {/* Current Team Members */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Team Members ({teamMembers.length})</h3>
              <div className="space-y-2">
                {teamMembers.map((member) => (
                  <div
                    key={member.user_id}
                    data-testid={`team-member-${member.user_id}`}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-gradient-to-r from-sky-50/80 to-indigo-50/60 dark:from-slate-700 dark:to-slate-700 border border-sky-100/80 dark:border-slate-600 rounded-xl gap-3"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {member.picture ? (
                        <img src={member.picture} alt={member.name} className="w-8 h-8 rounded-full flex-shrink-0" />
                      ) : (
                        <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-fuchsia-500 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
                          {member.name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900 dark:text-white truncate">{member.name}</p>
                        <p className="text-sm text-slate-600 dark:text-slate-400 truncate">{member.email}</p>
                        {/* Show business assignment for Super Admin */}
                        {isSuperAdmin && (
                          <p className="text-xs text-slate-500 dark:text-slate-500 flex items-center gap-1 mt-0.5">
                            <Building2 className="w-3 h-3" />
                            {member.business_name || "No business assigned"}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      <Badge className={ROLE_COLORS[member.role] || ROLE_COLORS.employee}>
                        {getRoleLabel(member.role)}
                      </Badge>
                      {/* Super Admin Quick Actions */}
                      {isSuperAdmin && member.user_id !== currentUser?.user_id && member.role !== "super_admin" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openAssignBusinessDialog(member)}
                          data-testid={`assign-business-${member.user_id}`}
                          className="rounded-full border-violet-200 text-violet-600 hover:bg-violet-50 dark:border-violet-700 dark:text-violet-400 dark:hover:bg-violet-900/30"
                          title="Assign Business & Role"
                        >
                          <Building2 className="w-4 h-4 mr-1" />
                          <span className="hidden sm:inline">Assign</span>
                        </Button>
                      )}
                      {isAdmin && member.user_id !== currentUser?.user_id && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openPermissionDialog(member)}
                            data-testid={`permissions-${member.user_id}`}
                            className="p-2 hover:bg-purple-100 dark:hover:bg-purple-900/30"
                            title="Manage Permissions"
                            disabled={member.role === "admin" || member.role === "super_admin" || !canManagePermissions}
                          >
                            <Shield className={`w-4 h-4 ${canManagePermissions ? 'text-purple-600 dark:text-purple-400' : 'text-slate-400'}`} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleGeneratePassword(member)}
                            data-testid={`reset-password-${member.user_id}`}
                            className="p-2 hover:bg-orange-100 dark:hover:bg-orange-900/30"
                            title="Reset Password"
                          >
                            <Key className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditUser(member)}
                            data-testid={`edit-user-${member.user_id}`}
                            className="p-2 hover:bg-indigo-100 dark:hover:bg-indigo-900/30"
                            title="Edit User"
                          >
                            <Edit className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteUser(member.user_id, member.name)}
                            data-testid={`delete-user-${member.user_id}`}
                            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30"
                            title="Delete User"
                          >
                            <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pending Invitations */}
            {invites.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Pending Invitations ({invites.length})</h3>
                <div className="space-y-2">
                  {invites.map((invite) => (
                    <div
                      key={invite.invite_id}
                      className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800"
                    >
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-amber-600" />
                        <p className="font-medium text-slate-900 dark:text-white">{invite.email}</p>
                        <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleCancelInvite(invite.invite_id)}
                        data-testid={`cancel-invite-${invite.invite_id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Invite Button */}
            <div className="flex gap-2">
              {isAdmin && (
                <Button
                  variant="outline"
                  data-testid="create-user-button"
                  onClick={() => setCreateUserDialogOpen(true)}
                  className="flex-1 rounded-full border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Create User
                </Button>
              )}
              <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    data-testid="invite-user-button"
                    className="flex-1 rounded-full border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Invite Team Member
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white dark:bg-slate-800 rounded-2xl">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-heading font-bold text-slate-900 dark:text-white">
                      Invite Team Member
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="invite-email" className="dark:text-slate-300">Email Address *</Label>
                      <Input
                        id="invite-email"
                        type="email"
                        data-testid="invite-email-input"
                        placeholder="colleague@example.com"
                        value={newInvite.email}
                        onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                        className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-white"
                      />
                    </div>
                    <div>
                      <Label htmlFor="invite-role" className="dark:text-slate-300">Role</Label>
                      <Select
                        value={newInvite.role}
                        onValueChange={(value) => setNewInvite({ ...newInvite, role: value })}
                      >
                        <SelectTrigger data-testid="invite-role-select" className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {availableRoles.map(role => (
                            <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={handleSendInvite}
                        data-testid="send-invite-button"
                        className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-3 font-semibold"
                      >
                        Send Invitation
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setInviteDialogOpen(false)}
                        className="flex-1 rounded-full border-slate-200"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </ColorCard>

        {/* Company Profile — logo + details used on salary slips (Admin/Manager) */}
        {(isAdmin || currentUser?.role === "manager") && (
          <div className="order-4">
            <CompanyProfileCard businessId={businessId} />
          </div>
        )}

      {/* Business Management (Super Admin Only) */}
      {canManageBusinesses && (
        <ColorCard
          theme="indigo"
          icon={Briefcase}
          title="Business Management"
          description="Create, edit, and organize your businesses"
          badge={<Badge className="bg-white/25 text-white border-0">Super Admin Only</Badge>}
          span
          className="order-7"
          testId="business-management-card"
        >
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* New Onboarding Wizard Button */}
              <Button
                onClick={() => setOnboardingWizardOpen(true)}
                data-testid="add-business-button"
                className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white rounded-full shadow-lg shadow-violet-200 dark:shadow-violet-900/30"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Add New Business
              </Button>
              
              {/* Quick Add Button (keeping old functionality) */}
              <Dialog open={businessDialogOpen} onOpenChange={setBusinessDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="rounded-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Quick Add
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white dark:bg-slate-800 rounded-2xl">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-heading font-bold text-slate-900 dark:text-white flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-violet-600" />
                      Quick Create Business
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="business-name-new" className="dark:text-slate-300">Business Name *</Label>
                      <Input
                        id="business-name-new"
                        data-testid="new-business-name-input"
                        placeholder="e.g., Acme Corporation"
                        value={newBusiness.name}
                        onChange={(e) => setNewBusiness({ ...newBusiness, name: e.target.value })}
                        className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-white"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="business-city" className="dark:text-slate-300">City</Label>
                        <Input
                          id="business-city"
                          data-testid="new-business-city-input"
                          placeholder="e.g., Mumbai"
                          value={newBusiness.city}
                          onChange={(e) => setNewBusiness({ ...newBusiness, city: e.target.value })}
                          className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-white"
                        />
                      </div>
                      <div>
                        <Label htmlFor="business-industry" className="dark:text-slate-300">Industry</Label>
                        <Input
                          id="business-industry"
                          data-testid="new-business-industry-input"
                          placeholder="e.g., Technology"
                          value={newBusiness.industry}
                          onChange={(e) => setNewBusiness({ ...newBusiness, industry: e.target.value })}
                          className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="business-status" className="dark:text-slate-300">Status</Label>
                      <Select
                        value={newBusiness.status}
                        onValueChange={(value) => setNewBusiness({ ...newBusiness, status: value })}
                      >
                        <SelectTrigger className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={handleCreateBusiness}
                        data-testid="create-business-button"
                        className="flex-1 bg-violet-600 hover:bg-violet-700 text-white rounded-full py-3 font-semibold"
                      >
                        Create Business
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setBusinessDialogOpen(false)}
                        className="flex-1 rounded-full border-slate-200"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              
              {/* Transfer Leads Dialog */}
              <Dialog open={transferLeadsDialogOpen} onOpenChange={setTransferLeadsDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    onClick={handleOpenTransferDialog}
                    data-testid="transfer-leads-button"
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-600 dark:text-amber-400 dark:hover:bg-amber-900/30 rounded-full"
                  >
                    <ArrowRightLeft className="w-4 h-4 mr-2" />
                    Transfer Leads
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white dark:bg-slate-800 rounded-2xl max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
                  <DialogHeader className="flex-shrink-0">
                    <DialogTitle className="text-2xl font-heading font-bold text-slate-900 dark:text-white flex items-center gap-2">
                      <ArrowRightLeft className="w-5 h-5 text-amber-600" />
                      Transfer Leads Between Businesses
                    </DialogTitle>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      Move leads and all related data (contacts, tasks, activities) to another business
                    </p>
                  </DialogHeader>
                  
                  <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
                    {/* Source Business Filter */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="flex-1">
                        <Label className="text-sm text-slate-600 dark:text-slate-400">Filter by Source Business</Label>
                        <Select value={sourceBusinessFilter} onValueChange={handleSourceBusinessFilterChange}>
                          <SelectTrigger className="mt-1 bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 rounded-xl">
                            <SelectValue placeholder="All Businesses" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Businesses</SelectItem>
                            {businesses.map((b) => (
                              <SelectItem key={b.business_id} value={b.business_id}>
                                {b.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1">
                        <Label className="text-sm text-slate-600 dark:text-slate-400">Target Business *</Label>
                        <Select value={targetBusinessId} onValueChange={setTargetBusinessId}>
                          <SelectTrigger className="mt-1 bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 rounded-xl">
                            <SelectValue placeholder="Select target business" />
                          </SelectTrigger>
                          <SelectContent>
                            {businesses.filter(b => b.status === "active").map((b) => (
                              <SelectItem key={b.business_id} value={b.business_id}>
                                {b.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {/* Selection Header */}
                    <div className="flex items-center justify-between py-2 border-b border-slate-200 dark:border-slate-600">
                      <button
                        onClick={handleSelectAllLeads}
                        className="flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
                      >
                        {selectedLeadsForTransfer.length === leadsForTransfer.length && leadsForTransfer.length > 0 ? (
                          <CheckSquare className="w-4 h-4" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                        {selectedLeadsForTransfer.length === leadsForTransfer.length ? "Deselect All" : "Select All"}
                      </button>
                      <span className="text-sm text-slate-500 dark:text-slate-400">
                        {selectedLeadsForTransfer.length} of {leadsForTransfer.length} selected
                      </span>
                    </div>
                    
                    {/* Leads List */}
                    <div className="flex-1 overflow-y-auto space-y-2 max-h-[300px]">
                      {leadsForTransfer.length === 0 ? (
                        <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                          <Users className="w-10 h-10 mx-auto mb-2 text-slate-300 dark:text-slate-600" />
                          <p>No leads found</p>
                        </div>
                      ) : (
                        leadsForTransfer.map((lead) => (
                          <div
                            key={lead.lead_id}
                            onClick={() => handleToggleLeadSelection(lead.lead_id)}
                            className={`p-3 rounded-xl border cursor-pointer transition-all ${
                              selectedLeadsForTransfer.includes(lead.lead_id)
                                ? "bg-indigo-50 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-600"
                                : "bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 hover:border-slate-300"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0">
                                {selectedLeadsForTransfer.includes(lead.lead_id) ? (
                                  <CheckSquare className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                                ) : (
                                  <Square className="w-5 h-5 text-slate-400" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-slate-900 dark:text-white">{lead.name}</span>
                                  <Badge className="text-xs bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300">
                                    {lead.stage}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 dark:text-slate-400">
                                  <span>{lead.email || "No email"}</span>
                                  <span>•</span>
                                  <span className="flex items-center gap-1">
                                    <Building2 className="w-3 h-3" />
                                    {lead.business_name}
                                  </span>
                                </div>
                              </div>
                              {lead.deal_value && (
                                <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                                  ₹{new Intl.NumberFormat('en-IN').format(lead.deal_value)}
                                </span>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    
                    {/* Warning */}
                    {selectedLeadsForTransfer.length > 0 && targetBusinessId && (
                      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-amber-700 dark:text-amber-300">
                            <p className="font-medium">This will transfer:</p>
                            <ul className="mt-1 space-y-0.5 text-amber-600 dark:text-amber-400">
                              <li>• {selectedLeadsForTransfer.length} lead(s)</li>
                              <li>• All related contacts</li>
                              <li>• All related tasks</li>
                              <li>• All activities & history</li>
                              <li>• All calendar events</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Actions */}
                    <div className="flex gap-3 pt-2 border-t border-slate-200 dark:border-slate-600">
                      <Button
                        onClick={handleTransferLeads}
                        disabled={selectedLeadsForTransfer.length === 0 || !targetBusinessId || transferLoading}
                        data-testid="confirm-transfer-button"
                        className="flex-1 bg-amber-600 hover:bg-amber-700 text-white rounded-full py-3 font-semibold disabled:opacity-50"
                      >
                        {transferLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Transferring...
                          </>
                        ) : (
                          <>
                            <ArrowRightLeft className="w-4 h-4 mr-2" />
                            Transfer {selectedLeadsForTransfer.length} Lead(s)
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setTransferLeadsDialogOpen(false)}
                        className="flex-1 rounded-full border-slate-200"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Business List */}
            <div className="space-y-3">
              {businesses.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Briefcase className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>No businesses created yet</p>
                </div>
              ) : (
                businesses.map((business) => (
                  <div
                    key={business.business_id}
                    data-testid={`business-${business.business_id}`}
                    className="p-4 bg-gradient-to-r from-violet-50/80 to-fuchsia-50/50 dark:from-slate-700 dark:to-slate-700 rounded-xl border border-violet-100 dark:border-slate-600"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold text-slate-900 dark:text-white">{business.name}</h4>
                          <Badge className={business.status === "active" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}>
                            {business.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-500 dark:text-slate-400 flex-wrap">
                          {business.city && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {business.city}
                            </span>
                          )}
                          {business.industry && (
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-3 h-3" /> {business.industry}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" /> {business.user_count || 0} users
                          </span>
                          <span className="text-indigo-600 font-medium">
                            {business.lead_count || 0} leads
                          </span>
                        </div>
                        {/* Public Registration URL */}
                        {business.slug && (
                          <div className="flex items-center gap-2 mt-2">
                            <Link2 className="w-3 h-3 text-green-600 flex-shrink-0" />
                            <span className="text-xs text-slate-500">Registration URL:</span>
                            <code className="text-xs bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded font-mono truncate max-w-[300px]" data-testid={`public-url-${business.business_id}`}>
                              {`${window.location.origin}/join/${business.slug}`}
                            </code>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 text-slate-400 hover:text-green-600"
                              data-testid={`copy-url-${business.business_id}`}
                              title="Copy registration URL"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(`${window.location.origin}/join/${business.slug}`);
                                toast.success("Registration URL copied!");
                              }}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openAssignUserDialog(business)}
                          data-testid={`assign-users-${business.business_id}`}
                          className="rounded-full border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                          title="Manage Users"
                        >
                          <Users className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setSelectedBusiness(business); setEditBusinessDialogOpen(true); }}
                          data-testid={`edit-business-${business.business_id}`}
                          className="rounded-full border-amber-200 text-amber-600 hover:bg-amber-50"
                          title="Edit Business"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setSelectedBusiness(business); setDeleteBusinessDialogOpen(true); }}
                          data-testid={`delete-business-${business.business_id}`}
                          className="rounded-full border-red-200 text-red-600 hover:bg-red-50"
                          title="Delete Business"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </ColorCard>
      )}

      {/* DATA — Task retention */}
      <ColorCard theme="emerald" icon={RotateCcw} title="Data & Retention" description="Housekeeping and archiving" className="order-5" testId="section-data">
        <TaskDataRetention currentUser={currentUser} />
      </ColorCard>

      </div>{/* /two-column grid */}

      {/* Edit Business Dialog */}
      <Dialog open={editBusinessDialogOpen} onOpenChange={setEditBusinessDialogOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-amber-600" />
              Edit Business
            </DialogTitle>
          </DialogHeader>
          {selectedBusiness && (
            <div className="space-y-4">
              <div>
                <Label className="dark:text-slate-300">Business Name *</Label>
                <Input
                  data-testid="edit-business-name-input"
                  value={selectedBusiness.name || ""}
                  onChange={(e) => setSelectedBusiness({ ...selectedBusiness, name: e.target.value })}
                  className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="dark:text-slate-300">City</Label>
                  <Input
                    data-testid="edit-business-city-input"
                    value={selectedBusiness.city || ""}
                    onChange={(e) => setSelectedBusiness({ ...selectedBusiness, city: e.target.value })}
                    className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-white"
                  />
                </div>
                <div>
                  <Label className="dark:text-slate-300">Industry</Label>
                  <Input
                    data-testid="edit-business-industry-input"
                    value={selectedBusiness.industry || ""}
                    onChange={(e) => setSelectedBusiness({ ...selectedBusiness, industry: e.target.value })}
                    className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-white"
                  />
                </div>
              </div>
              <div>
                <Label className="dark:text-slate-300">Status</Label>
                <Select
                  value={selectedBusiness.status || "active"}
                  onValueChange={(value) => setSelectedBusiness({ ...selectedBusiness, status: value })}
                >
                  <SelectTrigger className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleUpdateBusiness}
                  data-testid="update-business-button"
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white rounded-full py-3 font-semibold"
                >
                  Update Business
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setEditBusinessDialogOpen(false)}
                  className="flex-1 rounded-full border-slate-200"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Business Confirmation */}
      <AlertDialog open={deleteBusinessDialogOpen} onOpenChange={setDeleteBusinessDialogOpen}>
        <AlertDialogContent className="bg-white rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-heading font-bold text-slate-900 flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-600" />
              Delete Business
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              Are you sure you want to delete <span className="font-semibold">"{selectedBusiness?.name}"</span>?
              {selectedBusiness?.user_count > 0 && (
                <span className="block mt-2 text-red-600 font-medium">
                  Warning: This business has {selectedBusiness?.user_count} assigned user(s). Please unassign all users first.
                </span>
              )}
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full border-slate-200">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteBusiness}
              data-testid="confirm-delete-business"
              className="bg-red-600 hover:bg-red-700 text-white rounded-full"
              disabled={selectedBusiness?.user_count > 0}
            >
              Delete Business
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Assign Users Dialog */}
      <Dialog open={assignUserDialogOpen} onOpenChange={setAssignUserDialogOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 rounded-2xl max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" />
              Manage Users - {selectedBusiness?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Assigned Users */}
            <div>
              <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-2">Assigned Users ({businessUsers.length})</h4>
              {businessUsers.length === 0 ? (
                <p className="text-sm text-slate-500 py-2">No users assigned yet</p>
              ) : (
                <div className="space-y-2">
                  {businessUsers.map((user) => (
                    <div
                      key={user.user_id}
                      className="flex items-center justify-between p-3 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl"
                    >
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">{user.name}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUnassignUser(user.user_id)}
                        className="rounded-full border-red-200 text-red-600 hover:bg-red-50"
                        title="Unassign User"
                      >
                        <UserMinus className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Unassigned Users */}
            <div>
              <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-2">Available Users ({unassignedUsers.length})</h4>
              {unassignedUsers.length === 0 ? (
                <p className="text-sm text-slate-500 py-2">All users are assigned to businesses</p>
              ) : (
                <div className="space-y-2">
                  {unassignedUsers.map((user) => (
                    <div
                      key={user.user_id}
                      className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700 rounded-xl"
                    >
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">{user.name}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleAssignUser(user.user_id)}
                        className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white"
                        title="Assign User"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Assign
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button
              variant="outline"
              onClick={() => setAssignUserDialogOpen(false)}
              className="w-full rounded-full border-slate-200"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold text-slate-900 dark:text-white">
              Edit User
            </DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-user-name" className="dark:text-slate-300">Name</Label>
                <Input
                  id="edit-user-name"
                  data-testid="edit-user-name-input"
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                  className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-white"
                />
              </div>
              <div>
                <Label htmlFor="edit-user-email" className="dark:text-slate-300">Email (read-only)</Label>
                <Input
                  id="edit-user-email"
                  value={editingUser.email}
                  disabled
                  className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-slate-400 cursor-not-allowed"
                />
              </div>
              <div>
                <Label htmlFor="edit-user-role" className="dark:text-slate-300">Role</Label>
                <Select
                  value={editingUser.role}
                  onValueChange={(value) => setEditingUser({ ...editingUser, role: value })}
                >
                  <SelectTrigger data-testid="edit-user-role-select" className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRoles.map(role => (
                      <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Payroll profile — appears on the salary slip */}
              <div className="pt-2 border-t border-slate-100 dark:border-slate-700">
                <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400 mb-2">Payroll profile</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="edit-user-empid" className="dark:text-slate-300">Employee ID</Label>
                    <Input
                      id="edit-user-empid"
                      data-testid="edit-user-empid-input"
                      value={editingUser.employee_id || ""}
                      onChange={(e) => setEditingUser({ ...editingUser, employee_id: e.target.value })}
                      placeholder="e.g. DNX-0043"
                      className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-user-doj" className="dark:text-slate-300">Date of Joining</Label>
                    <Input
                      id="edit-user-doj"
                      type="date"
                      data-testid="edit-user-doj-input"
                      value={editingUser.date_of_joining || ""}
                      onChange={(e) => setEditingUser({ ...editingUser, date_of_joining: e.target.value })}
                      className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-white"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="edit-user-designation" className="dark:text-slate-300">Designation</Label>
                    <Input
                      id="edit-user-designation"
                      data-testid="edit-user-designation-input"
                      value={editingUser.designation || ""}
                      onChange={(e) => setEditingUser({ ...editingUser, designation: e.target.value })}
                      placeholder="e.g. Senior Designer"
                      className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-white"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleUpdateUser}
                  data-testid="update-user-button"
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-3 font-semibold"
                >
                  Update User
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setEditUserDialogOpen(false)}
                  className="flex-1 rounded-full border-slate-200"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createUserDialogOpen} onOpenChange={setCreateUserDialogOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold text-slate-900 dark:text-white">
              Create New User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="create-user-name" className="dark:text-slate-300">Name *</Label>
              <Input
                id="create-user-name"
                data-testid="create-user-name-input"
                placeholder="John Doe"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-white"
              />
            </div>
            <div>
              <Label htmlFor="create-user-email" className="dark:text-slate-300">Email *</Label>
              <Input
                id="create-user-email"
                type="email"
                data-testid="create-user-email-input"
                placeholder="john@company.com"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-white"
              />
            </div>
            <div>
              <Label htmlFor="create-user-role" className="dark:text-slate-300">Role</Label>
              <Select
                value={newUser.role}
                onValueChange={(value) => setNewUser({ ...newUser, role: value })}
              >
                <SelectTrigger data-testid="create-user-role-select" className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(role => (
                    <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
              <p className="text-sm text-slate-700 dark:text-slate-300">
                ℹ️ A temporary password will be automatically generated and displayed after creation.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleCreateUser}
                data-testid="create-user-submit-button"
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-3 font-semibold"
              >
                Create User
              </Button>
              <Button
                variant="outline"
                onClick={() => setCreateUserDialogOpen(false)}
                className="flex-1 rounded-full border-slate-200"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold text-slate-900 dark:text-white">
              Password Generated
            </DialogTitle>
          </DialogHeader>
          {passwordResetUser && (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                <p className="text-sm text-slate-700 dark:text-slate-300 mb-2">
                  A temporary password has been generated for:
                </p>
                <p className="font-semibold text-slate-900 dark:text-white">
                  {passwordResetUser.name} ({passwordResetUser.email})
                </p>
              </div>

              <div>
                <Label className="dark:text-slate-300">Temporary Password</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={generatedPassword}
                    readOnly
                    className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-white font-mono text-lg"
                  />
                  <Button
                    onClick={() => copyToClipboard(generatedPassword)}
                    data-testid="copy-password-button"
                    variant="outline"
                    className="rounded-xl"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  ℹ️ <strong>Important:</strong> Share this password securely with the user. They should change it after first login. All existing sessions have been terminated.
                </p>
              </div>

              <Button
                onClick={() => {
                  setPasswordDialogOpen(false);
                  setGeneratedPassword("");
                  setPasswordResetUser(null);
                }}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-3 font-semibold"
              >
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Permission Management Dialog */}
      <Dialog open={permissionDialogOpen} onOpenChange={(open) => {
        setPermissionDialogOpen(open);
        if (!open) {
          setSelectedUserForPermission(null);
          setUserPermissions([]);
        }
      }}>
        <DialogContent className="bg-white dark:bg-slate-800 rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-600" />
              Page Permissions
            </DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400">
              {selectedUserForPermission && (
                <>Manage page access for <strong>{selectedUserForPermission.name}</strong></>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {permissionLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* User Info */}
              {selectedUserForPermission && (
                <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-semibold">
                    {selectedUserForPermission.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">{selectedUserForPermission.name}</p>
                    <div className="flex items-center gap-2">
                      <Badge className={ROLE_COLORS[selectedUserForPermission.role] || ROLE_COLORS.employee}>
                        {getRoleLabel(selectedUserForPermission.role)}
                      </Badge>
                    </div>
                  </div>
                </div>
              )}

              {/* Admin Warning */}
              {selectedUserForPermission?.role === "admin" && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    ⚠️ Admin users always have full access to all pages.
                  </p>
                </div>
              )}

              {/* Page Permissions List */}
              {selectedUserForPermission?.role !== "admin" && selectedUserForPermission?.role !== "super_admin" && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-semibold">Page Access</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleResetPermissions}
                      className="text-xs text-slate-500 hover:text-slate-700"
                    >
                      <RotateCcw className="w-3 h-3 mr-1" />
                      Reset to Defaults
                    </Button>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {availablePages.map(page => (
                      <div 
                        key={page.value}
                        className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">{page.label}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{page.description}</p>
                        </div>
                        <Switch
                          checked={userPermissions.includes(page.value)}
                          onCheckedChange={() => handlePermissionToggle(page.value)}
                          data-testid={`permission-toggle-${page.value}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {selectedUserForPermission?.role !== "admin" && selectedUserForPermission?.role !== "super_admin" && (
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={() => setPermissionDialogOpen(false)}
                    variant="outline"
                    className="flex-1 rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSavePermissions}
                    disabled={permissionLoading}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full"
                    data-testid="save-permissions-button"
                  >
                    {permissionLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Shield className="w-4 h-4 mr-2" />
                    )}
                    Save Permissions
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Business & Role Dialog (Super Admin) */}
      <Dialog open={assignBusinessDialogOpen} onOpenChange={(open) => {
        setAssignBusinessDialogOpen(open);
        if (!open) {
          setSelectedUserForAssignment(null);
          setSelectedRoleForAssignment("");
        }
      }}>
        <DialogContent className="bg-white dark:bg-slate-800 rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-violet-600" />
              Manage User Assignment
            </DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400">
              Update business and role for this user
            </DialogDescription>
          </DialogHeader>
          
          {selectedUserForAssignment && (
            <div className="space-y-4">
              {/* User Info */}
              <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg flex items-center gap-3">
                {selectedUserForAssignment.picture ? (
                  <img src={selectedUserForAssignment.picture} alt={selectedUserForAssignment.name} className="w-10 h-10 rounded-full" />
                ) : (
                  <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-semibold">
                    {selectedUserForAssignment.name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">{selectedUserForAssignment.name}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{selectedUserForAssignment.email}</p>
                </div>
              </div>

              {/* Role Selection */}
              <div>
                <Label className="dark:text-slate-300">Role</Label>
                <Select
                  value={selectedRoleForAssignment}
                  onValueChange={setSelectedRoleForAssignment}
                >
                  <SelectTrigger 
                    data-testid="assign-role-select"
                    className="mt-1 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-white"
                  >
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRoles.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${
                            role.value === "super_admin" ? "bg-red-500" :
                            role.value === "admin" ? "bg-purple-500" :
                            role.value === "manager" ? "bg-indigo-500" :
                            role.value === "project_manager" ? "bg-blue-500" :
                            role.value === "team_leader" ? "bg-cyan-500" : "bg-green-500"
                          }`} />
                          {role.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Business Selection */}
              <div>
                <Label className="dark:text-slate-300">Assigned Business</Label>
                <Select
                  value={selectedBusinessForAssignment || "none"}
                  onValueChange={(value) => setSelectedBusinessForAssignment(value === "none" ? "" : value)}
                >
                  <SelectTrigger 
                    data-testid="assign-business-select"
                    className="mt-1 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-white"
                  >
                    <SelectValue placeholder="Select business" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <span className="text-slate-500">No business assigned</span>
                    </SelectItem>
                    {allBusinessesList.filter(b => b.status === "active").map((business) => (
                      <SelectItem key={business.business_id} value={business.business_id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-3 h-3 text-violet-500" />
                          {business.name}
                          {business.city && <span className="text-xs text-slate-400">({business.city})</span>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Current Assignment Info */}
              <div className="p-3 bg-slate-100 dark:bg-slate-600/50 rounded-lg text-sm">
                <p className="text-slate-600 dark:text-slate-300">
                  <span className="font-medium">Current:</span> {getRoleLabel(selectedUserForAssignment.role)} 
                  {selectedUserForAssignment.business_name && (
                    <span> at {selectedUserForAssignment.business_name}</span>
                  )}
                  {!selectedUserForAssignment.business_name && (
                    <span className="text-slate-400"> (no business)</span>
                  )}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => setAssignBusinessDialogOpen(false)}
                  variant="outline"
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAssignBusinessAndRole}
                  className="flex-1 bg-violet-600 hover:bg-violet-700 text-white rounded-full"
                  data-testid="save-assignment-button"
                >
                  <UserCheck className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Business Onboarding Wizard */}
      <BusinessOnboardingWizard
        open={onboardingWizardOpen}
        onClose={() => setOnboardingWizardOpen(false)}
        onSuccess={() => {
          fetchBusinesses();
          toast.success("Business created successfully!");
        }}
      />
    </div>
  );
};

export default Settings;
